/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  ApprovalDto,
  DecideApprovalDto,
  EditSlideDto,
  ListApprovalsParams,
  ReelEditDto,
  ReelEditStateDto,
  RerollSlideDto,
  ReviewSummaryDto,
  ReviewSummaryParams,
  SaveReelEditDto,
  SlidePreviewDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The review queue (Flows › Drafts): waiting items oldest first, or reviewed ones newest first.
 */
export const listApprovals = (
  id: string,
  params?: ListApprovalsParams,
  signal?: AbortSignal,
) => {
  return customInstance<ApprovalDto[]>({
    url: `/api/v1/workspaces/${id}/approvals`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListApprovalsQueryKey = (id: string, params?: ListApprovalsParams) => {
  return [`/api/v1/workspaces/${id}/approvals`, ...(params ? [params] : [])] as const;
};

export const getListApprovalsQueryOptions = <
  TData = Awaited<ReturnType<typeof listApprovals>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListApprovalsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListApprovalsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listApprovals>>> = ({
    signal,
  }) => listApprovals(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListApprovalsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listApprovals>>
>;
export type ListApprovalsQueryError = ErrorType<ApiErrorDto>;

export function useListApprovals<
  TData = Awaited<ReturnType<typeof listApprovals>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | ListApprovalsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApprovals>>,
          TError,
          Awaited<ReturnType<typeof listApprovals>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApprovals<
  TData = Awaited<ReturnType<typeof listApprovals>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListApprovalsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApprovals>>,
          TError,
          Awaited<ReturnType<typeof listApprovals>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApprovals<
  TData = Awaited<ReturnType<typeof listApprovals>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListApprovalsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The review queue (Flows › Drafts): waiting items oldest first, or reviewed ones newest first.
 */

export function useListApprovals<
  TData = Awaited<ReturnType<typeof listApprovals>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListApprovalsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApprovals>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListApprovalsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Home's "Needs your review": drafts held by a guardrail, then the queue, and how many wait.
 */
export const reviewSummary = (
  id: string,
  params?: ReviewSummaryParams,
  signal?: AbortSignal,
) => {
  return customInstance<ReviewSummaryDto>({
    url: `/api/v1/workspaces/${id}/approvals/summary`,
    method: 'GET',
    params,
    signal,
  });
};

export const getReviewSummaryQueryKey = (id: string, params?: ReviewSummaryParams) => {
  return [
    `/api/v1/workspaces/${id}/approvals/summary`,
    ...(params ? [params] : []),
  ] as const;
};

export const getReviewSummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof reviewSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ReviewSummaryParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getReviewSummaryQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof reviewSummary>>> = ({
    signal,
  }) => reviewSummary(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ReviewSummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof reviewSummary>>
>;
export type ReviewSummaryQueryError = ErrorType<ApiErrorDto>;

export function useReviewSummary<
  TData = Awaited<ReturnType<typeof reviewSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | ReviewSummaryParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reviewSummary>>,
          TError,
          Awaited<ReturnType<typeof reviewSummary>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReviewSummary<
  TData = Awaited<ReturnType<typeof reviewSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ReviewSummaryParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reviewSummary>>,
          TError,
          Awaited<ReturnType<typeof reviewSummary>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReviewSummary<
  TData = Awaited<ReturnType<typeof reviewSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ReviewSummaryParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Home's "Needs your review": drafts held by a guardrail, then the queue, and how many wait.
 */

export function useReviewSummary<
  TData = Awaited<ReturnType<typeof reviewSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ReviewSummaryParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reviewSummary>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getReviewSummaryQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Approve (it continues through the flow) or reject a draft.
 */
export const decideApproval = (
  id: string,
  approvalId: string,
  decideApprovalDto: BodyType<DecideApprovalDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/decision`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: decideApprovalDto,
    signal,
  });
};

export const getDecideApprovalMutationKey = () => ['decideApproval'] as const;

export const getDecideApprovalMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof decideApproval>>,
    TError,
    DecideApprovalMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof decideApproval>>,
  TError,
  DecideApprovalMutationVariables,
  TContext
> => {
  const mutationKey = getDecideApprovalMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof decideApproval>>,
    DecideApprovalMutationVariables
  > = (props) => {
    const { id, approvalId, data } = props ?? {};

    return decideApproval(id, approvalId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type DecideApprovalMutationResult = NonNullable<
  Awaited<ReturnType<typeof decideApproval>>
>;
export type DecideApprovalMutationBody = BodyType<DecideApprovalDto>;
export type DecideApprovalMutationError = ErrorType<ApiErrorDto>;
export type DecideApprovalMutationVariables = {
  id: string;
  approvalId: string;
  data: BodyType<DecideApprovalDto>;
};

/**
 * @summary Approve (it continues through the flow) or reject a draft.
 */
export const useDecideApproval = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof decideApproval>>,
      TError,
      DecideApprovalMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof decideApproval>>,
  TError,
  DecideApprovalMutationVariables,
  TContext
> => {
  return useMutation(getDecideApprovalMutationOptions(options), queryClient);
};
/**
 * @summary Swap a waiting carousel's slide for another image and/or caption from its slide pools.
 */
export const rerollSlide = (
  id: string,
  approvalId: string,
  rerollSlideDto: BodyType<RerollSlideDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ApprovalDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reroll`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: rerollSlideDto,
    signal,
  });
};

export const getRerollSlideMutationKey = () => ['rerollSlide'] as const;

export const getRerollSlideMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof rerollSlide>>,
    TError,
    RerollSlideMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof rerollSlide>>,
  TError,
  RerollSlideMutationVariables,
  TContext
> => {
  const mutationKey = getRerollSlideMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof rerollSlide>>,
    RerollSlideMutationVariables
  > = (props) => {
    const { id, approvalId, data } = props ?? {};

    return rerollSlide(id, approvalId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RerollSlideMutationResult = NonNullable<
  Awaited<ReturnType<typeof rerollSlide>>
>;
export type RerollSlideMutationBody = BodyType<RerollSlideDto>;
export type RerollSlideMutationError = ErrorType<ApiErrorDto>;
export type RerollSlideMutationVariables = {
  id: string;
  approvalId: string;
  data: BodyType<RerollSlideDto>;
};

/**
 * @summary Swap a waiting carousel's slide for another image and/or caption from its slide pools.
 */
export const useRerollSlide = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof rerollSlide>>,
      TError,
      RerollSlideMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof rerollSlide>>,
  TError,
  RerollSlideMutationVariables,
  TContext
> => {
  return useMutation(getRerollSlideMutationOptions(options), queryClient);
};
/**
 * @summary Put a chosen caption, your own words or another image on one slide of a waiting carousel.
 */
export const editSlide = (
  id: string,
  approvalId: string,
  slide: number,
  editSlideDto: BodyType<EditSlideDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ApprovalDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/slides/${slide}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: editSlideDto,
    signal,
  });
};

export const getEditSlideMutationKey = () => ['editSlide'] as const;

export const getEditSlideMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof editSlide>>,
    TError,
    EditSlideMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof editSlide>>,
  TError,
  EditSlideMutationVariables,
  TContext
> => {
  const mutationKey = getEditSlideMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof editSlide>>,
    EditSlideMutationVariables
  > = (props) => {
    const { id, approvalId, slide, data } = props ?? {};

    return editSlide(id, approvalId, slide, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type EditSlideMutationResult = NonNullable<Awaited<ReturnType<typeof editSlide>>>;
export type EditSlideMutationBody = BodyType<EditSlideDto>;
export type EditSlideMutationError = ErrorType<ApiErrorDto>;
export type EditSlideMutationVariables = {
  id: string;
  approvalId: string;
  slide: number;
  data: BodyType<EditSlideDto>;
};

/**
 * @summary Put a chosen caption, your own words or another image on one slide of a waiting carousel.
 */
export const useEditSlide = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof editSlide>>,
      TError,
      EditSlideMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof editSlide>>,
  TError,
  EditSlideMutationVariables,
  TContext
> => {
  return useMutation(getEditSlideMutationOptions(options), queryClient);
};
/**
 * @summary Draw a slide with a change without keeping it - review's preview before Apply.
 */
export const previewSlide = (
  id: string,
  approvalId: string,
  slide: number,
  editSlideDto: BodyType<EditSlideDto>,
  signal?: AbortSignal,
) => {
  return customInstance<SlidePreviewDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/slides/${slide}/preview`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: editSlideDto,
    signal,
  });
};

export const getPreviewSlideMutationKey = () => ['previewSlide'] as const;

export const getPreviewSlideMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewSlide>>,
    TError,
    PreviewSlideMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewSlide>>,
  TError,
  PreviewSlideMutationVariables,
  TContext
> => {
  const mutationKey = getPreviewSlideMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewSlide>>,
    PreviewSlideMutationVariables
  > = (props) => {
    const { id, approvalId, slide, data } = props ?? {};

    return previewSlide(id, approvalId, slide, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewSlideMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewSlide>>
>;
export type PreviewSlideMutationBody = BodyType<EditSlideDto>;
export type PreviewSlideMutationError = ErrorType<ApiErrorDto>;
export type PreviewSlideMutationVariables = {
  id: string;
  approvalId: string;
  slide: number;
  data: BodyType<EditSlideDto>;
};

/**
 * @summary Draw a slide with a change without keeping it - review's preview before Apply.
 */
export const usePreviewSlide = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof previewSlide>>,
      TError,
      PreviewSlideMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof previewSlide>>,
  TError,
  PreviewSlideMutationVariables,
  TContext
> => {
  return useMutation(getPreviewSlideMutationOptions(options), queryClient);
};
/**
 * @summary A waiting reel to edit: its source, the live edit and every saved version.
 */
export const getReelEdit = (id: string, approvalId: string, signal?: AbortSignal) => {
  return customInstance<ReelEditStateDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edit`,
    method: 'GET',
    signal,
  });
};

export const getGetReelEditQueryKey = (id: string, approvalId: string) => {
  return [`/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edit`] as const;
};

export const getGetReelEditQueryOptions = <
  TData = Awaited<ReturnType<typeof getReelEdit>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReelEditQueryKey(id, approvalId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReelEdit>>> = ({ signal }) =>
    getReelEdit(id, approvalId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && approvalId !== null && approvalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetReelEditQueryResult = NonNullable<Awaited<ReturnType<typeof getReelEdit>>>;
export type GetReelEditQueryError = ErrorType<ApiErrorDto>;

export function useGetReelEdit<
  TData = Awaited<ReturnType<typeof getReelEdit>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReelEdit>>,
          TError,
          Awaited<ReturnType<typeof getReelEdit>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReelEdit<
  TData = Awaited<ReturnType<typeof getReelEdit>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReelEdit>>,
          TError,
          Awaited<ReturnType<typeof getReelEdit>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReelEdit<
  TData = Awaited<ReturnType<typeof getReelEdit>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A waiting reel to edit: its source, the live edit and every saved version.
 */

export function useGetReelEdit<
  TData = Awaited<ReturnType<typeof getReelEdit>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEdit>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReelEditQueryOptions(id, approvalId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save an edit as a new version and render it. When it's done, the draft
shows it - poll the version until it's DONE or FAILED.
 */
export const saveReelEdit = (
  id: string,
  approvalId: string,
  saveReelEditDto: BodyType<SaveReelEditDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ReelEditDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edits`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: saveReelEditDto,
    signal,
  });
};

export const getSaveReelEditMutationKey = () => ['saveReelEdit'] as const;

export const getSaveReelEditMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveReelEdit>>,
    TError,
    SaveReelEditMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveReelEdit>>,
  TError,
  SaveReelEditMutationVariables,
  TContext
> => {
  const mutationKey = getSaveReelEditMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveReelEdit>>,
    SaveReelEditMutationVariables
  > = (props) => {
    const { id, approvalId, data } = props ?? {};

    return saveReelEdit(id, approvalId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveReelEditMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveReelEdit>>
>;
export type SaveReelEditMutationBody = BodyType<SaveReelEditDto>;
export type SaveReelEditMutationError = ErrorType<ApiErrorDto>;
export type SaveReelEditMutationVariables = {
  id: string;
  approvalId: string;
  data: BodyType<SaveReelEditDto>;
};

/**
 * @summary Save an edit as a new version and render it. When it's done, the draft
shows it - poll the version until it's DONE or FAILED.
 */
export const useSaveReelEdit = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveReelEdit>>,
      TError,
      SaveReelEditMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveReelEdit>>,
  TError,
  SaveReelEditMutationVariables,
  TContext
> => {
  return useMutation(getSaveReelEditMutationOptions(options), queryClient);
};
export const getReelEditVersion = (
  id: string,
  approvalId: string,
  editId: string,
  signal?: AbortSignal,
) => {
  return customInstance<ReelEditDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edits/${editId}`,
    method: 'GET',
    signal,
  });
};

export const getGetReelEditVersionQueryKey = (
  id: string,
  approvalId: string,
  editId: string,
) => {
  return [
    `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edits/${editId}`,
  ] as const;
};

export const getGetReelEditVersionQueryOptions = <
  TData = Awaited<ReturnType<typeof getReelEditVersion>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  editId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetReelEditVersionQueryKey(id, approvalId, editId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReelEditVersion>>> = ({
    signal,
  }) => getReelEditVersion(id, approvalId, editId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null &&
      id !== undefined &&
      approvalId !== null &&
      approvalId !== undefined &&
      editId !== null &&
      editId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetReelEditVersionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReelEditVersion>>
>;
export type GetReelEditVersionQueryError = ErrorType<ApiErrorDto>;

export function useGetReelEditVersion<
  TData = Awaited<ReturnType<typeof getReelEditVersion>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  editId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReelEditVersion>>,
          TError,
          Awaited<ReturnType<typeof getReelEditVersion>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReelEditVersion<
  TData = Awaited<ReturnType<typeof getReelEditVersion>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  editId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReelEditVersion>>,
          TError,
          Awaited<ReturnType<typeof getReelEditVersion>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReelEditVersion<
  TData = Awaited<ReturnType<typeof getReelEditVersion>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  editId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetReelEditVersion<
  TData = Awaited<ReturnType<typeof getReelEditVersion>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  editId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReelEditVersion>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReelEditVersionQueryOptions(id, approvalId, editId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Put an earlier rendered version back on the draft.
 */
export const restoreReelEdit = (
  id: string,
  approvalId: string,
  editId: string,
  signal?: AbortSignal,
) => {
  return customInstance<ReelEditStateDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edits/${editId}/restore`,
    method: 'POST',
    signal,
  });
};

export const getRestoreReelEditMutationKey = () => ['restoreReelEdit'] as const;

export const getRestoreReelEditMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof restoreReelEdit>>,
    TError,
    RestoreReelEditMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof restoreReelEdit>>,
  TError,
  RestoreReelEditMutationVariables,
  TContext
> => {
  const mutationKey = getRestoreReelEditMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof restoreReelEdit>>,
    RestoreReelEditMutationVariables
  > = (props) => {
    const { id, approvalId, editId } = props ?? {};

    return restoreReelEdit(id, approvalId, editId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RestoreReelEditMutationResult = NonNullable<
  Awaited<ReturnType<typeof restoreReelEdit>>
>;

export type RestoreReelEditMutationError = ErrorType<ApiErrorDto>;
export type RestoreReelEditMutationVariables = {
  id: string;
  approvalId: string;
  editId: string;
};

/**
 * @summary Put an earlier rendered version back on the draft.
 */
export const useRestoreReelEdit = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof restoreReelEdit>>,
      TError,
      RestoreReelEditMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof restoreReelEdit>>,
  TError,
  RestoreReelEditMutationVariables,
  TContext
> => {
  return useMutation(getRestoreReelEditMutationOptions(options), queryClient);
};
/**
 * @summary Put the reel back as the flow made it.
 */
export const restoreReelOriginal = (
  id: string,
  approvalId: string,
  signal?: AbortSignal,
) => {
  return customInstance<ReelEditStateDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/reel-edit/restore-original`,
    method: 'POST',
    signal,
  });
};

export const getRestoreReelOriginalMutationKey = () => ['restoreReelOriginal'] as const;

export const getRestoreReelOriginalMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof restoreReelOriginal>>,
    TError,
    RestoreReelOriginalMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof restoreReelOriginal>>,
  TError,
  RestoreReelOriginalMutationVariables,
  TContext
> => {
  const mutationKey = getRestoreReelOriginalMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof restoreReelOriginal>>,
    RestoreReelOriginalMutationVariables
  > = (props) => {
    const { id, approvalId } = props ?? {};

    return restoreReelOriginal(id, approvalId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RestoreReelOriginalMutationResult = NonNullable<
  Awaited<ReturnType<typeof restoreReelOriginal>>
>;

export type RestoreReelOriginalMutationError = ErrorType<ApiErrorDto>;
export type RestoreReelOriginalMutationVariables = { id: string; approvalId: string };

/**
 * @summary Put the reel back as the flow made it.
 */
export const useRestoreReelOriginal = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof restoreReelOriginal>>,
      TError,
      RestoreReelOriginalMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof restoreReelOriginal>>,
  TError,
  RestoreReelOriginalMutationVariables,
  TContext
> => {
  return useMutation(getRestoreReelOriginalMutationOptions(options), queryClient);
};
